import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { HelpIcon } from '../HelpIcon';
import { TooltipProvider } from '../ui/tooltip';
import { getHelpContent } from '../../lib/helpContent';

interface TemplateEmptyStateProps {
  type: 'no-templates' | 'no-results' | 'error';
  onCreateTemplate?: () => void;
  onClearFilters?: () => void;
  errorMessage?: string;
}

export const TemplateEmptyState: React.FC<TemplateEmptyStateProps> = ({
  type,
  onCreateTemplate,
  onClearFilters,
  errorMessage,
}) => {
  const navigate = useNavigate();

  const handleCreateTemplate = () => {
    if (onCreateTemplate) {
      onCreateTemplate();
    } else {
      navigate('/templates/new');
    }
  };

  if (type === 'error') {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading templates</h3>
        <p className="mt-1 text-sm text-gray-500">
          {errorMessage || 'An unexpected error occurred while loading templates.'}
        </p>
        <div className="mt-6">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (type === 'no-results') {
    return (
      <div className="text-center py-12">
        <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
        <p className="mt-1 text-sm text-gray-500">
          We couldn't find any templates matching your search criteria.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          {onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Clear filters
            </button>
          )}
          <button
            type="button"
            onClick={handleCreateTemplate}
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Create template
          </button>
        </div>
      </div>
    );
  }

  // Default: no-templates
  const templateVariablesHelp = getHelpContent('templateVariables');
  const templateBestPracticesHelp = getHelpContent('templateBestPractices');
  
  return (
    <TooltipProvider>
      <div className="text-center py-12">
        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
        <div className="flex items-center justify-center gap-2 mt-2">
          <h3 className="text-sm font-medium text-gray-900">No templates</h3>
          {templateVariablesHelp && (
            <HelpIcon
              tooltip="Learn about templates"
              modalTitle={templateVariablesHelp.title}
              modalContent={
                <div>
                  <p className="mb-4">{templateVariablesHelp.content}</p>
                  {templateBestPracticesHelp && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold mb-2">{templateBestPracticesHelp.title}</h4>
                      <p>{templateBestPracticesHelp.content}</p>
                    </div>
                  )}
                </div>
              }
              size="sm"
              variant="subtle"
            />
          )}
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Get started by creating your first email or SMS template.
        </p>
        <div className="mt-6">
          <button
            type="button"
            onClick={handleCreateTemplate}
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Create your first template
          </button>
        </div>
        <div className="mt-8 border-t border-gray-200 pt-8">
          <div className="mx-auto max-w-2xl">
            <div className="flex items-center justify-center gap-2 mb-4">
              <h4 className="text-sm font-medium text-gray-900">What are templates?</h4>
              {templateVariablesHelp && (
                <HelpIcon
                  tooltip="Learn more about templates"
                  modalTitle={templateVariablesHelp.title}
                  modalContent={
                    <div>
                      <p className="mb-4">{templateVariablesHelp.content}</p>
                      {templateBestPracticesHelp && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-semibold mb-2">{templateBestPracticesHelp.title}</h4>
                          <p>{templateBestPracticesHelp.content}</p>
                        </div>
                      )}
                    </div>
                  }
                  size="sm"
                  variant="subtle"
                />
              )}
            </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 text-left">
            <div className="rounded-lg bg-gray-50 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h5 className="text-sm font-medium text-gray-900">Email Templates</h5>
                  <p className="mt-1 text-sm text-gray-500">
                    Create reusable email templates with variables for personalization.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h5 className="text-sm font-medium text-gray-900">SMS Templates</h5>
                  <p className="mt-1 text-sm text-gray-500">
                    Create short, effective SMS messages for quick communication.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            <p>
              Templates can be used in campaigns to send personalized messages to your contacts.
              Use variables like <code className="text-indigo-600 bg-indigo-50 px-1 py-0.5 rounded">{'{{contact.first_name}}'}</code> to customize each message.
            </p>
          </div>
        </div>
        </div>
      </div>
      </TooltipProvider>
  );
};

